import { createGlobalStyle } from 'styled-components';
import { getLuminance, readableColor, transparentize } from 'polished';
import palette from './theme/palette';

interface Props {
  background?: string;
  accent?: string;
}

// Compute companion adaptive tokens off the creator's chosen page background + accent color.
// These flow into CSS variables on `:root` so any styled-component on the share page can pick
// them up and stay readable regardless of light/dark backgrounds or which CTA color the
// creator picked.
const safeLuminance = (color: string): number => {
  try { return getLuminance(color); } catch { return 1; }
};

const buildAdaptiveTokens = (rawBg: string | undefined, rawAccent: string | undefined) => {
  const bg = rawBg || '#fff';
  const accent = rawAccent || palette.primary.main;
  const dark = safeLuminance(bg) < 0.5;
  return {
    accent,
    foreground: readableColor(bg, palette.common.black, palette.common.white, true),
    foregroundMuted: dark
      ? transparentize(0.4, palette.common.white)
      : palette.naturalGray,
    foregroundSubtle: dark
      ? transparentize(0.6, palette.common.white)
      : palette.friarGray,
    border: dark
      ? transparentize(0.85, palette.common.white)
      : palette.pampas,
    hover: dark
      ? transparentize(0.92, palette.common.white)
      : palette.springWood,
    // Active-line wash: a translucent version of the resolved accent. Brighter on dark so it
    // remains visible against a darker page; subtle on light to keep the page calm.
    activeWash: dark
      ? transparentize(0.78, accent)
      : transparentize(0.92, accent),
    scrollbarThumb: dark
      ? transparentize(0.8, palette.common.white)
      : palette.swissCoffee,
  };
};

export default createGlobalStyle<Props>`
  * {
    box-sizing: border-box;
    padding: 0px;
    margin: 0px;
    -webkit-tap-highlight-color: transparent;
    scrollbar-width: none;
  }

  *::-webkit-scrollbar {
    display: none;
  }

  html {
    font-size: 10px;
    font-family: 'Inter UI', sans-serif;
    background-color: ${({ background }) => background || '#ffffff'};
  }

  @supports (font-variation-settings: normal) {
    html { font-family: 'Inter UI', sans-serif; }
  }

  body {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }


  :root {
    --ud-color-sh1: #6140FF;
    --ud-color-sh2: linear-gradient(0deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.08)), #6140FF;
    --ud-color-sh3: linear-gradient(0deg, #5136D6, #5136D6), #6140FF;
    --uo-color-sh1: transparent;
    --uo-color-sh2: rgba(97, 64, 255, 0.02);
    --uo-color-sh3: rgba(97, 64, 255, 0.06);
    --uo-color-sh4: rgba(97, 64, 255, 0.08);
    --color-dark-base: #000000;
    --color-light-base: #FFFFFF;
    --color-red-base: #FB634F;

    --color-base: #FEFAF5;
    --grey-medium: #6E697E;
    --grey-dark: #DDDDDD;
    --border-radius: 4px;
    --global-background: ${({ background }) => background || '#fff'};
    /* Adaptive tokens derived from the creator's selected background + accent (CTA) color.
       Consumers (e.g. SidePanel, TranscriptPanel) read these CSS vars so text/border/hover
       stay readable on cream, white, or dark backgrounds, and accent-tinted elements
       follow the creator's CTA color instead of stranding a snozzberry on a foreign-themed
       page. */
    --global-accent: ${({ background, accent }) => buildAdaptiveTokens(background, accent).accent};
    --global-foreground: ${({ background, accent }) => buildAdaptiveTokens(background, accent).foreground};
    --global-foreground-muted: ${({ background, accent }) => buildAdaptiveTokens(background, accent).foregroundMuted};
    --global-foreground-subtle: ${({ background, accent }) => buildAdaptiveTokens(background, accent).foregroundSubtle};
    --global-border: ${({ background, accent }) => buildAdaptiveTokens(background, accent).border};
    --global-hover: ${({ background, accent }) => buildAdaptiveTokens(background, accent).hover};
    --global-active-wash: ${({ background, accent }) => buildAdaptiveTokens(background, accent).activeWash};
    --global-scrollbar-thumb: ${({ background, accent }) => buildAdaptiveTokens(background, accent).scrollbarThumb};
    }
`;
